// Estimates (Work Orders › New › Estimate, and the saved-estimates list): built up in memory,
// saved as its own EST-numbered record, printable as an estimate or invoice document, and
// convertible into a work order. Split out of app.js (2026-10-03).
//
// app.js values this needs (its database URL, the signed-in user's data, the letterhead
// address, and a few work-order helpers) come in through setEstimateDeps(), which app.js
// calls at startup. That avoids a circular import with app.js, whose top-level await makes
// two-way imports fragile.
import { auth, database, functions, storage } from './initFirebase';
import { ref, get, onChildAdded, onChildChanged, onChildRemoved } from 'firebase/database';
import { glow, liveScope, patchHTML, pointerHeld, recentInteraction } from './liveView';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { openSignLog } from './signLog';
import { toCents, toDollars, sumCents, lineCents, priceTotals, formatMoney } from './money';
import { usesDockmaster } from './marinaConfig';
import { blockIfReadOnly, escapeHTML, fetchIdToken, generateId, idbDelete, idbGet, idbPut, nextEstimateNumber, saveEstimateRecord, toastMessage } from './utilities';
import { formatDateToYYYYMMDD, parseDay } from './date-format';
import { openCompose, pagesToPdf, customerContact } from './mailClient';
import { openEmailHistory } from './emailHistory';
import { decisionLabel } from './estimateLinesCore';

let shell = null;
export function setEstimateDeps(deps) { shell = deps; }

// ============================================================
// ESTIMATES (Type: Estimate) — same context strip / op picker as a
// work order, but built up in memory and saved as its own EST-numbered
// record instead of writing each operation straight to Firebase.
// See design_handoff_estimates_mooring_pesticide/README.md.
// ============================================================

const ESTIMATE_DEFAULTS_FALLBACK = {
  salesTaxRate: 0.08625,
  environmentalFeeRate: 0.015,
  depositPercent: 50,
  validForDays: 30,
  termsText: 'Estimate valid 30 days from date issued. Work begins on receipt of deposit. Final invoice reflects actual hours and materials.'
};

let estimateDefaults = { ...ESTIMATE_DEFAULTS_FALLBACK };

async function loadEstimateDefaults() {
  try {
    const idToken = await fetchIdToken();
    const res = await fetch(`${shell.currentURL}/pricing/estimateDefaults.json?auth=${idToken}`);
    const data = await res.json();
    if (data) estimateDefaults = { ...ESTIMATE_DEFAULTS_FALLBACK, ...data };
  } catch (e) {
    console.error('Error loading estimate defaults, using fallback:', e);
  }
}
document.addEventListener('DOMContentLoaded', () => shell.waitForAuthUser().then(loadEstimateDefaults));

export function isEstimateMode() {
  return document.getElementById('work-order-type-select')?.value === 'estimate';
}

function newEstimateState() {
  return {
    id: generateId(),
    Estimate_Number: null,
    Status: 'Draft',
    Operations: [],
    Parts: [],
    Pricing: null,
    Created_Date: new Date().toISOString(),
  };
}

let estimateState = newEstimateState();

export function resetEstimateState() {
  estimateState = newEstimateState();
  const shortDesc = document.getElementById('estimateShortDesc');
  const validFor = document.getElementById('estimateValidFor');
  const deposit = document.getElementById('estimateDeposit');
  const terms = document.getElementById('estimateTerms');
  const numberDisplay = document.getElementById('estimate-number-display');
  const pill = document.getElementById('estimate-status-pill');
  if (shortDesc) shortDesc.value = '';
  if (validFor) validFor.value = String(estimateDefaults.validForDays);
  if (deposit) deposit.value = String(estimateDefaults.depositPercent);
  if (terms) terms.value = estimateDefaults.termsText;
  if (numberDisplay) numberDisplay.textContent = 'Assigned on save';
  if (pill) {
    pill.textContent = 'Draft';
    pill.className = 'wo-status-pill wo-status-pill-draft' + (isEstimateMode() ? '' : ' hidden-form-section');
  }
  renderEstimateOperations();
  renderEstimateParts();
  recalcEstimatePricing();
}

// ---------- Operations ----------

export function renderEstimateOperations() {
  const list = document.getElementById('estimate-ops-list');
  if (!list) return;
  list.innerHTML = '';
  estimateState.Operations.forEach(op => {
    const row = document.createElement('div');
    row.className = 'wo-estimate-op-row';
    row.innerHTML = `
      <div class="wo-estimate-op-row-main">
        <div class="wo-estimate-op-row-name">${escapeHTML(op.Description || '')}</div>
        <div class="wo-estimate-op-row-code">${escapeHTML(op.OpCode || '')}</div>
      </div>
      <span class="wo-estimate-op-row-hrs">${Number(op.Hours || 0).toFixed(1)}</span>
      <span class="wo-estimate-op-row-amt">$${Number(op.Amount || 0).toFixed(2)}</span>
      <button type="button" class="wo-estimate-op-row-remove" data-op-id="${op.id}">&times;</button>
    `;
    list.appendChild(row);
    // Choices the customer picks one of in the portal (e.g. Rebuild / Replace), or "Not now".
    const opts = document.createElement('div');
    opts.className = 'wo-estimate-op-opts';
    opts.innerHTML = `${(op.Options || []).map((o, n) => `<span class="wo-estimate-op-opt">${escapeHTML(o.Label)} · $${Number(o.Amount || 0).toFixed(2)}<button type="button" data-opt-remove="${op.id}:${n}" aria-label="Remove ${escapeHTML(o.Label)}">&times;</button></span>`).join('')}
      <span class="wo-estimate-op-opt-form hidden" data-opt-form="${op.id}"><input type="text" placeholder="Choice, e.g. Replace" maxlength="40"><input type="number" placeholder="Price" min="0" step="0.01"><button type="button" data-opt-save="${op.id}">Add</button></span>
      <button type="button" class="wo-estimate-op-opt-add" data-opt-add="${op.id}">+ Add a choice</button>`;
    list.appendChild(opts);
  });
  list.querySelectorAll('[data-opt-add]').forEach(btn => {
    btn.addEventListener('click', () => {
      const form = list.querySelector(`[data-opt-form="${btn.dataset.optAdd}"]`);
      form.classList.remove('hidden');
      btn.classList.add('hidden');
      form.querySelector('input').focus();
    });
  });
  list.querySelectorAll('[data-opt-save]').forEach(btn => {
    btn.addEventListener('click', () => {
      const op = estimateState.Operations.find(o => o.id === btn.dataset.optSave);
      const [label, price] = btn.parentElement.querySelectorAll('input');
      if (!op || !label.value.trim()) { toastMessage('Name the choice, e.g. Rebuild or Replace', 'red'); return; }
      op.Options = [...(op.Options || []), { Label: label.value.trim().slice(0, 40), Amount: Math.round((Number(price.value) || 0) * 100) / 100 }];
      renderEstimateOperations();
    });
  });
  list.querySelectorAll('[data-opt-remove]').forEach(btn => {
    btn.addEventListener('click', () => {
      const [id, n] = btn.dataset.optRemove.split(':');
      const op = estimateState.Operations.find(o => o.id === id);
      if (op) op.Options = (op.Options || []).filter((_, i) => i !== Number(n));
      renderEstimateOperations();
    });
  });

  list.querySelectorAll('.wo-estimate-op-row-remove').forEach(btn => {
    btn.addEventListener('click', () => {
      estimateState.Operations = estimateState.Operations.filter(o => o.id !== btn.dataset.opId);
      renderEstimateOperations();
      recalcEstimatePricing();
    });
  });

  if (isEstimateMode()) {
    const countEl = document.getElementById('wo-ops-count');
    const hoursEl = document.getElementById('wo-ops-hours');
    const totalHours = estimateState.Operations.reduce((sum, o) => sum + Number(o.Hours || 0), 0);
    if (countEl) countEl.textContent = estimateState.Operations.length ? `${estimateState.Operations.length} added` : '';
    if (hoursEl) hoursEl.textContent = totalHours > 0 ? `${totalHours.toFixed(1)} hrs` : '';
  }
}

export function addEstimateOperation() {
  const opCode = document.getElementById('opCode').value;
  const shortDesc = document.getElementById('dmShortDesc').value;
  const description = document.getElementById('description').value;
  const opType = document.getElementById('opType').value;
  const hours = parseFloat(document.getElementById('estimatedHours').value) || 0;
  const amount = parseFloat(document.getElementById('estimatedCharge').value) || 0;

  const label = shortDesc || description;
  if (!label) {
    toastMessage('Add Short Description', 'red');
    return;
  }

  estimateState.Operations.push({
    id: generateId(),
    OpCode: opCode,
    Description: label,
    // The longer description, when there is one: the customer's "why" in the portal.
    ...(shortDesc && description && description.trim() !== shortDesc.trim() ? { Details: description.trim() } : {}),
    Op_Type: opType,
    Hours: hours,
    Amount: amount
  });

  // Clear the form the same way addJob() does — otherwise the next op
  // picked reopens this panel with the previous op's leftover values
  // (short description, hours, charge) still sitting in the fields.
  document.getElementById('opCode').value = '';
  document.getElementById('opType').value = '';
  document.getElementById('dmShortDesc').value = '';
  document.getElementById('description').value = '';
  document.getElementById('estimatedHours').value = '';
  document.getElementById('estimatedCharge').value = '';

  const jobEl = document.getElementById('job');
  if (jobEl) jobEl.style.display = 'none';
  renderEstimateOperations();
  recalcEstimatePricing();
}

// ---------- Parts ----------

function renderEstimateParts() {
  const list = document.getElementById('estimate-parts-list');
  if (!list) return;
  list.innerHTML = '';
  estimateState.Parts.forEach(p => {
    const row = document.createElement('div');
    row.className = 'wo-parts-row';
    const ext = Number(p.Qty || 0) * Number(p.Unit_Price || 0);
    row.innerHTML = `
      <span class="wo-parts-row-pn${p.manual ? ' manual' : ''}">${escapeHTML(p.Part_Number || '')}</span>
      <span class="wo-parts-row-desc">${escapeHTML(p.Description || '')}</span>
      <span class="wo-parts-row-vendor">${escapeHTML(p.Vendor || '')}</span>
      <span class="wo-parts-row-qty">${Number(p.Qty || 0)}</span>
      <span class="wo-parts-row-unit">$${Number(p.Unit_Price || 0).toFixed(2)}</span>
      <span class="wo-parts-row-ext">$${ext.toFixed(2)}</span>
      <button type="button" class="wo-parts-row-remove" data-part-id="${p.id}">&times;</button>
    `;
    list.appendChild(row);
  });

  list.querySelectorAll('.wo-parts-row-remove').forEach(btn => {
    btn.addEventListener('click', () => {
      estimateState.Parts = estimateState.Parts.filter(p => p.id !== btn.dataset.partId);
      renderEstimateParts();
      recalcEstimatePricing();
    });
  });

  const countEl = document.getElementById('estimate-parts-count');
  const totalEl = document.getElementById('estimate-parts-total-header');
  const partsTotal = estimateState.Parts.reduce((sum, p) => sum + Number(p.Qty || 0) * Number(p.Unit_Price || 0), 0);
  if (countEl) countEl.textContent = estimateState.Parts.length ? `${estimateState.Parts.length} lines` : '';
  if (totalEl) totalEl.textContent = estimateState.Parts.length ? `$${partsTotal.toFixed(2)}` : '';
}

async function openPartsPicker() {
  const overlay = document.getElementById('estimate-parts-picker-overlay');
  const input = document.getElementById('estimate-parts-picker-search');
  if (!overlay || !input) return;
  overlay.classList.remove('hidden');
  input.value = '';
  input.focus();
  await renderPartsPickerResults('');
}

function closePartsPicker() {
  document.getElementById('estimate-parts-picker-overlay')?.classList.add('hidden');
}

async function renderPartsPickerResults(query) {
  const body = document.getElementById('estimate-parts-picker-body');
  const countEl = document.getElementById('estimate-parts-picker-count');
  if (!body) return;
  const inventory = await shell.fetchDataFromStore('inventoryData');
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const filtered = (terms.length
    ? inventory.filter(item => {
        const values = Object.values(item).map(v => (v == null ? '' : v.toString().toLowerCase()));
        return terms.every(term => values.some(v => v.includes(term)));
      })
    : inventory
  ).slice(0, 30);

  if (countEl) countEl.textContent = query ? `${filtered.length} results` : '';

  if (!filtered.length) {
    body.innerHTML = `<div class="wo-picker-empty">No matching parts.</div>`;
    return;
  }

  body.innerHTML = filtered.map(item => {
    const qty = Number(item.Qty) || 0;
    let stockClass = 'in-stock', stockLabel = `${qty} in stock`;
    if (qty <= 0) { stockClass = 'out-of-stock'; stockLabel = 'Out of stock'; }
    else if (qty <= 2) { stockClass = 'low-stock'; }
    const vendor = Array.isArray(item.Vendor_List) ? (item.Vendor_List[0] || '') : (item.Vendor_List || '');
    return `
      <div class="wo-parts-picker-result" data-part-id="${item.id}">
        <span class="wo-parts-picker-result-pn">${escapeHTML(item.Part_Number || '')}</span>
        <div class="wo-parts-picker-result-main">
          <div class="wo-parts-picker-result-desc">${escapeHTML(item.Description || '')}</div>
          <div class="wo-parts-picker-result-sub">${escapeHTML(vendor)}${item.Location ? ' &middot; ' + escapeHTML(item.Location) : ''}</div>
        </div>
        <span class="wo-parts-picker-result-stock ${stockClass}">${stockLabel}</span>
        <span class="wo-parts-picker-result-price">$${Number(item.Sale_Price || 0).toFixed(2)}</span>
      </div>
    `;
  }).join('');

  body.querySelectorAll('.wo-parts-picker-result').forEach(el => {
    el.addEventListener('click', () => {
      const item = filtered.find(i => i.id === el.dataset.partId);
      if (item) selectInventoryPart(item);
    });
  });
}

function selectInventoryPart(item) {
  const vendor = Array.isArray(item.Vendor_List) ? (item.Vendor_List[0] || '') : (item.Vendor_List || '');
  estimateState.Parts.push({
    id: generateId(),
    Part_Number: item.Part_Number || '',
    Description: item.Description || '',
    Vendor: vendor,
    Qty: 1,
    Unit_Price: Number(item.Sale_Price) || 0,
    manual: false,
    inventoryId: item.id
  });
  closePartsPicker();
  renderEstimateParts();
  recalcEstimatePricing();
}

function openManualPartForm() {
  closePartsPicker();
  document.getElementById('estimate-parts-manual-overlay')?.classList.remove('hidden');
  const ids = ['estimatePartManualNumber', 'estimatePartManualDesc', 'estimatePartManualVendor'];
  ids.forEach(id => { const el = document.getElementById(id); if (el) el.value = ''; });
  const qty = document.getElementById('estimatePartManualQty');
  const price = document.getElementById('estimatePartManualPrice');
  if (qty) qty.value = '1';
  if (price) price.value = '0';
}

function closeManualPartForm() {
  document.getElementById('estimate-parts-manual-overlay')?.classList.add('hidden');
}

function addManualPartLine() {
  const desc = document.getElementById('estimatePartManualDesc').value.trim();
  if (!desc) {
    toastMessage('Enter a description', 'red');
    return;
  }
  estimateState.Parts.push({
    id: generateId(),
    Part_Number: document.getElementById('estimatePartManualNumber').value.trim(),
    Description: desc,
    Vendor: document.getElementById('estimatePartManualVendor').value.trim(),
    Qty: parseFloat(document.getElementById('estimatePartManualQty').value) || 0,
    Unit_Price: parseFloat(document.getElementById('estimatePartManualPrice').value) || 0,
    manual: true
  });
  closeManualPartForm();
  renderEstimateParts();
  recalcEstimatePricing();
}

// ---------- Pricing ----------
// Environmental fee and sales tax are both computed off the (labor + parts)
// subtotal, not compounded — verified against the design mockup's numbers
// (labor $1725 + parts $531.96 = $2256.96; ×1.5% = $33.85 fee, ×8.625% =
// $194.66 tax; total $2485.47; deposit at 50% = $1242.74).
function recalcEstimatePricing() {
  const laborRateEl = document.getElementById('currentLaborRate');
  const laborRate = laborRateEl ? parseFloat(laborRateEl.value) || 0 : 0;
  // In cents (money.js): each fee/tax is rounded to the cent, so the printed lines add up to the total.
  const laborCents = sumCents(estimateState.Operations.map(o => toCents(o.Amount)));
  const partsCents = sumCents(estimateState.Parts.map(p => lineCents(p.Qty, toCents(p.Unit_Price))));
  const totalHours = estimateState.Operations.reduce((sum, o) => sum + Number(o.Hours || 0), 0);

  const envFeeRate = estimateDefaults.environmentalFeeRate;
  const taxRate = estimateDefaults.salesTaxRate;
  const depositPercent = parseFloat(document.getElementById('estimateDeposit')?.value) || 0;
  const t = priceTotals({ subtotal: laborCents + partsCents, taxRate, feeRate: envFeeRate, depositPercent });
  const laborTotal = toDollars(laborCents);
  const partsTotal = toDollars(partsCents);
  const envFee = toDollars(t.fee);
  const tax = toDollars(t.tax);
  const total = toDollars(t.total);
  const depositDue = toDollars(t.deposit);

  const setText = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text; };
  const pct = (r) => Number((r * 100).toFixed(3));
  setText('estimate-labor-label', `Labor · ${totalHours.toFixed(1)} @ $${laborRate.toFixed(0)}`);
  setText('estimate-labor-total', `$${laborTotal.toFixed(2)}`);
  setText('estimate-parts-subtotal', `$${partsTotal.toFixed(2)}`);
  setText('estimate-tax-fee-label', `Tax ${pct(taxRate)}% + fee ${pct(envFeeRate)}%`);
  setText('estimate-tax-fee-total', `$${(envFee + tax).toFixed(2)}`);
  setText('estimate-grand-total', `$${total.toFixed(2)}`);
  setText('estimate-deposit-label', `Deposit due · ${depositPercent}%`);
  setText('estimate-deposit-total', `$${depositDue.toFixed(2)}`);

  estimateState.Pricing = {
    laborTotal, partsTotal, environmentalFeeRate: envFeeRate, environmentalFee: envFee,
    salesTaxRate: taxRate, salesTax: tax, total, depositPercent, depositDue
  };
  return estimateState.Pricing;
}

// ---------- Numbering + save ----------
// nextEstimateNumber() and saveEstimateRecord() live in utilities.js —
// mooringEstimator.js (a separate webpack bundle) needs the same
// numbering/save path and utilities.js is the one shared module every
// page already imports from.

// Builds the persistable/printable record from the in-progress creation
// screen state. Shared by saveEstimate() and the print/PDF preview so both
// always agree on what an estimate "is" at a given moment.
function buildEstimateRecordFromState(customerId, boatId, pricing) {
  return {
    id: estimateState.id,
    Estimate_Number: estimateState.Estimate_Number,
    Status: estimateState.Status || 'Draft',
    Customer_ID: customerId,
    Customer_Name: document.getElementById('work-order-form-customer-name').value,
    Customer_DMid: document.getElementById('work-order-form-customer-DMid').value,
    Boat_ID: boatId,
    Boat_DMid: document.getElementById('work-order-form-boat-DMid').value,
    Boat_Name: document.getElementById('work-order-form-boat-name').value,
    Short_Description: document.getElementById('estimateShortDesc').value,
    Valid_For_Days: parseInt(document.getElementById('estimateValidFor').value, 10) || 30,
    Deposit_Percent: parseInt(document.getElementById('estimateDeposit').value, 10) || 0,
    Terms_Text: document.getElementById('estimateTerms').value,
    Operations: estimateState.Operations,
    Parts: estimateState.Parts,
    Pricing: pricing,
    Created_Date: estimateState.Created_Date,
    Updated_Date: new Date().toISOString(),
  };
}

export async function saveEstimate() {
  if (blockIfReadOnly('Saving an estimate')) return;

  const customerId = document.getElementById('work-order-form-customer-id').value;
  const boatId = document.getElementById('work-order-form-boat-id').value;
  if (!customerId || !boatId) {
    toastMessage('Select a customer and boat first', 'red');
    document.getElementById('wo-context-change-btn')?.click();
    return;
  }
  if (!estimateState.Operations.length) {
    toastMessage('Add at least one operation', 'red');
    return;
  }

  const pricing = recalcEstimatePricing();
  const saveBtn = document.getElementById('wo-create-done-btn');
  const originalLabel = saveBtn ? saveBtn.textContent : '';
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }

  try {
    if (!estimateState.Estimate_Number) {
      estimateState.Estimate_Number = await nextEstimateNumber();
    }

    const record = buildEstimateRecordFromState(customerId, boatId, pricing);
    estimateState = { ...estimateState, ...record };

    await saveEstimateRecord(record);

    const numberDisplay = document.getElementById('estimate-number-display');
    if (numberDisplay) numberDisplay.textContent = record.Estimate_Number;
    toastMessage(`Estimate ${record.Estimate_Number} saved`, 'green');

    document.getElementById('Add').style.display = 'none';
  } catch (e) {
    console.error('Error saving estimate:', e);
    toastMessage('Error saving estimate', 'red');
  } finally {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = isEstimateMode() ? 'Save estimate' : originalLabel; }
  }
}

// ---------- Estimate UI wiring ----------

document.getElementById('estimate-add-part-btn')?.addEventListener('click', openPartsPicker);
document.getElementById('estimate-parts-picker-close')?.addEventListener('click', closePartsPicker);
document.getElementById('estimate-parts-picker-overlay')?.addEventListener('click', (e) => {
  if (e.target.id === 'estimate-parts-picker-overlay') closePartsPicker();
});
document.getElementById('estimate-parts-picker-manual-link')?.addEventListener('click', openManualPartForm);
document.getElementById('estimate-parts-manual-close')?.addEventListener('click', closeManualPartForm);
document.getElementById('estimate-parts-manual-overlay')?.addEventListener('click', (e) => {
  if (e.target.id === 'estimate-parts-manual-overlay') closeManualPartForm();
});
document.getElementById('estimatePartManualAddBtn')?.addEventListener('click', addManualPartLine);
document.getElementById('estimateDeposit')?.addEventListener('change', recalcEstimatePricing);

let estimatePartsSearchDebounce = null;
document.getElementById('estimate-parts-picker-search')?.addEventListener('input', function () {
  clearTimeout(estimatePartsSearchDebounce);
  const query = this.value;
  estimatePartsSearchDebounce = setTimeout(() => renderPartsPickerResults(query), 150);
});

// ---------- Printable document ----------


export function fmtEstDocDate(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

let estDocRecord = null;   // what the open estimate document shows (for Email)
let estDocValidThrough = null;

function openEstimateDocument(record) {
  const container = document.getElementById('estimateContainer1');
  if (!container || !record) return;
  estDocRecord = record;

  const setText = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text ?? ''; };
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const pct = (r) => Number((Number(r || 0) * 100).toFixed(3));

  const issuedDate = new Date();
  const validThrough = new Date(issuedDate.getTime() + (Number(record.Valid_For_Days) || 30) * 86400000);
  estDocValidThrough = validThrough;

  setText('estDocNumber1', record.Estimate_Number);
  setText('estDocCustomerName', record.Customer_Name);
  setText('estDocCustomerContact', document.getElementById('work-order-form-customer-phone')?.value || '');
  setText('estDocBoatName', record.Boat_Name);
  setText('estDocBoatSub', document.getElementById('wo-context-mooring')?.textContent || '');
  setText('estDocDateIssued', fmtEstDocDate(issuedDate));
  setText('estDocValidThrough', fmtEstDocDate(validThrough));
  setText('estDocPreparedBy', auth?.currentUser?.displayName || auth?.currentUser?.email || '');

  const laborRate = parseFloat(document.getElementById('currentLaborRate')?.value) || 0;
  const opsBody = document.getElementById('estDocOpsBody');
  if (opsBody) {
    opsBody.innerHTML = record.Operations.map(op => `
      <tr>
        <td>
          <div class="estDoc-op-name">${escapeHTML(op.Description || '')}</div>
          ${op.Op_Type ? `<div class="estDoc-op-desc">${escapeHTML(op.Op_Type)}</div>` : ''}
          ${(op.Options || []).length ? `<div class="estDoc-op-desc">Choose one: ${op.Options.map(o => `${escapeHTML(o.Label)} ${money(o.Amount)}`).join(' · ')}</div>` : ''}
        </td>
        <td class="estDoc-num">${Number(op.Hours || 0).toFixed(1)}</td>
        <td class="estDoc-num">$${laborRate.toFixed(0)}</td>
        <td class="estDoc-num">${money(op.Amount)}</td>
      </tr>
    `).join('');
  }

  const pricing = record.Pricing || {};
  setText('estDocLaborSubtotal', money(pricing.laborTotal));
  setText('estDocPartsSubtotal', money(pricing.partsTotal));
  setText('estDocEnvFeeLabel', `Environmental fee (${pct(pricing.environmentalFeeRate)}%)`);
  setText('estDocEnvFee', money(pricing.environmentalFee));
  setText('estDocTaxLabel', `Sales tax (${pct(pricing.salesTaxRate)}%)`);
  setText('estDocSalesTax', money(pricing.salesTax));
  setText('estDocTotal', money(pricing.total));
  setText('estDocDepositLabel', `Deposit to begin work (${record.Deposit_Percent}%)`);
  setText('estDocDeposit', money(pricing.depositDue));
  setText('estDocTerms', record.Terms_Text);

  setText('estDocFooterAddress1', shell.letterheadAddress);
  setText('estDocFooterAddress2', shell.letterheadAddress);
  setText('estDocPage2Sub', `${record.Estimate_Number} · ${record.Boat_Name} · ${record.Customer_Name}`);

  const partsBody = document.getElementById('estDocPartsBody');
  if (partsBody) {
    partsBody.innerHTML = record.Parts.map(p => `
      <tr>
        <td class="${p.manual ? 'manual' : ''}">${escapeHTML(p.Part_Number || '')}</td>
        <td>${escapeHTML(p.Description || '')}</td>
        <td>${escapeHTML(p.Vendor || '')}</td>
        <td class="estDoc-num">${Number(p.Qty || 0)}</td>
        <td class="estDoc-num">${money(p.Unit_Price)}</td>
        <td class="estDoc-num">${money(Number(p.Qty || 0) * Number(p.Unit_Price || 0))}</td>
      </tr>
    `).join('');
  }
  setText('estDocPartsSubtotal2', money(pricing.partsTotal));
  document.getElementById('estDocPage2')?.classList.toggle(
    'estDoc-hide-vendor',
    !record.Parts.some(p => (p.Vendor || '').trim())
  );

  container.classList.remove('hidden');
}

function closeEstimateDocument() {
  document.getElementById('estimateContainer1')?.classList.add('hidden');
}

document.getElementById('estDocCloseButton')?.addEventListener('click', closeEstimateDocument);
document.getElementById('estDocPrintButton')?.addEventListener('click', () => window.print());

// Email the open document as a PDF from the company account (Settings › Email).
// The words come from the template (Settings › Email › Templates); vals fill its {fields}.
async function emailDocument({ pages, label, customerId, docType, templateId, vals, links, recordType, recordId, onSent, afterSend }) {
  const pdf = pagesToPdf(pages, `${label}.pdf`.replace(/[\\/:*?"<>|]+/g, '-'));
  const contact = await customerContact(customerId);
  return openCompose({
    to: contact.email, templateId, vals, links,
    attachments: pdf,
    customerId, docType, docLabel: label, recordType, recordId, onSent, afterSend,
  });
}

document.getElementById('estDocEmailButton')?.addEventListener('click', async () => {
  const r = estDocRecord;
  if (!r) return;
  const label = `Estimate ${r.Estimate_Number || ''}`.trim();
  const total = `$${Number(r.Pricing?.total || 0).toFixed(2)}`;
  // A link to approve and sign online, without signing in (functions/signLinks.js, roadmap 4.1).
  // It's made only when the email actually goes out.
  const canSign = r.id && (r.Status === 'Draft' || r.Status === 'Sent');
  emailDocument({
    pages: [...document.querySelectorAll('#estimateContainer1 .estDoc-page')],
    label, customerId: r.Customer_ID, docType: 'estimate', recordType: 'estimate', recordId: r.id || null,
    templateId: 'estimate',
    vals: {
      estimate_no: r.Estimate_Number || '', total, ...(r.Boat_Name ? { boat: r.Boat_Name } : {}),
      ...(estDocValidThrough ? { valid_through: fmtEstDocDate(estDocValidThrough) } : {}),
    },
    links: canSign ? { sign_link: () => callFn('createSignLink', { kind: 'estimate', estimateId: r.id }).then(x => x?.url) } : {},
    // Emailing a saved draft is sending it.
    ...(r.id && r.Status === 'Draft' ? {
      onSent: () => transitionEstimateStatus(r, 'Sent'),
      afterSend: { kind: 'estimateSent', id: r.id }, // the same, for Send later (functions/emailScheduled.js)
    } : {}),
  });
});

// Emails: what's been sent about this estimate (emailHistory.js).
document.getElementById('estDocEmailsButton')?.addEventListener('click', () => {
  const r = estDocRecord;
  if (!r) return;
  if (!r.id) { toastMessage('Save the estimate first. Its emails show here once it has been sent.', 'blue', 4000); return; }
  openEmailHistory({ recordId: r.id }, {
    title: `Estimate ${r.Estimate_Number || ''}`.trim(), firstName: String(r.Customer_Name || '').split(' ')[0],
    sub: [r.Customer_Name, r.Boat_Name].filter(Boolean).join(' · '),
  });
});

// Invoice print/PDF document. Invoices are created in DockMaster only — this
// is a read-only print face for one, built from the exact same data
// openWoInvoicePreview() already fetches (getCustomerBalance/getWorkOrderInfoDM).
// It must never write anything or fabricate a total.
let invDocRecord = null;   // what the open invoice document shows (for Email)

/** Hide the parts only the app's own invoices use (so a DockMaster invoice shows as before). */
function resetAppInvoiceParts() {
  ['invDocSubtotalRow', 'invDocFeeRow', 'invDocTaxRow', 'invDocPaidRow', 'invDocNotes']
    .forEach(id => document.getElementById(id)?.classList.add('hidden'));
  document.getElementById('invDocPastDuePill')?.classList.remove('invDoc-pill-draft', 'invDoc-pill-void', 'invDoc-pill-paid');
}

export function openInvoiceDocument(record) {
  const container = document.getElementById('invoiceContainer1');
  if (!container || !record) return;
  invDocRecord = record;
  resetAppInvoiceParts();

  const setText = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text ?? ''; };
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  setText('invDocNumber', `INV-${record.Id}`);
  setText('invDocCustomerName', record.CustomerName || '');
  setText('invDocCustomerContact', record.CustomerContact || '');
  setText('invDocBoatName', record.BoatName || '');
  setText('invDocBoatSub', record.BoatSub || '');
  setText('invDocDateIssued', fmtEstDocDate(new Date()));
  setText('invDocDueDate', record.DueDate ? fmtEstDocDate(parseDay(record.DueDate)) : '—');
  setText('invDocWorkOrder', record.WorkOrder ? `WO-${record.WorkOrder}` : '');

  const pastDuePill = document.getElementById('invDocPastDuePill');
  const daysPastDue = Number(record.DaysPastDue) || 0;
  if (pastDuePill) {
    if (daysPastDue > 0) {
      pastDuePill.textContent = `PAST DUE · ${daysPastDue} DAYS`;
      pastDuePill.classList.remove('hidden');
    } else {
      pastDuePill.classList.add('hidden');
    }
  }

  const estimateRef = document.getElementById('invDocEstimateRef');
  if (estimateRef) {
    if (record.EstimateNumber) {
      estimateRef.textContent = `From estimate ${record.EstimateNumber}`;
      estimateRef.classList.remove('hidden');
    } else {
      estimateRef.classList.add('hidden');
    }
  }

  const lineItems = (record.LineItems && record.LineItems.length)
    ? record.LineItems
    : [{ Description: record.Description || '', Charge: record.InvoiceAmount }];

  const lineSum = lineItems.reduce((sum, li) => sum + (parseFloat(String(li.Charge).replace(/[^0-9.-]/g, '')) || 0), 0);
  const invoiceAmount = Number(record.InvoiceAmount) || 0;

  const linesBody = document.getElementById('invDocLinesBody');
  if (linesBody) {
    linesBody.innerHTML = lineItems.map(li => {
      const descParts = (li.Description || '').split(':');
      const name = (descParts[0] || '').trim();
      const sub = descParts.slice(1).join(':').trim();
      const charge = typeof li.Charge === 'number' ? money(li.Charge) : (li.Charge || money(0));
      return `
        <tr>
          <td><div class="invDoc-line-name">${escapeHTML(name)}</div>${sub ? `<div class="invDoc-line-sub">${escapeHTML(sub)}</div>` : ''}</td>
          <td class="estDoc-num">${escapeHTML(String(charge))}</td>
        </tr>
      `;
    }).join('');
  }

  // Never print InvoiceAmount and a line-sum that disagree — print InvoiceAmount
  // (the source of truth) and log the mismatch instead of silently preferring the sum.
  if (invoiceAmount && Math.abs(invoiceAmount - lineSum) > 0.01) {
    console.warn(`Invoice ${record.Id}: line-item sum (${lineSum.toFixed(2)}) does not match InvoiceAmount (${invoiceAmount.toFixed(2)}) — printing InvoiceAmount.`);
  }
  const total = invoiceAmount || lineSum;
  setText('invDocInvoiceTotal', money(total));

  const depositRow = document.getElementById('invDocDepositRow');
  let balanceDue = total;
  if (record.DepositReceived && Number(record.DepositReceived.amount)) {
    setText('invDocDepositLabel', `Deposit received${record.DepositReceived.date ? ' — ' + record.DepositReceived.date : ''}`);
    setText('invDocDeposit', `-${money(record.DepositReceived.amount)}`);
    depositRow?.classList.remove('hidden');
    balanceDue = total - Number(record.DepositReceived.amount);
  } else {
    depositRow?.classList.add('hidden');
  }
  setText('invDocBalanceDue', money(balanceDue));
  setText('invDocPaymentDueBy', record.DueDate ? `Payment due by ${fmtEstDocDate(parseDay(record.DueDate))}` : '');
  setText('invDocTerms', `Payment due ${record.DueDate ? fmtEstDocDate(parseDay(record.DueDate)) : 'upon receipt'}. A 1.5% monthly finance charge applies to past-due balances. Pay by check or card; reference INV-${record.Id} with payment.`);
  setText('invDocFooterAddress', shell.letterheadAddress);

  container.classList.remove('hidden');
}

/**
 * The printable face of one of the app's own invoices (functions/invoices.js, roadmap Phase 1):
 * every amount comes from the saved invoice (server-computed, in cents); nothing is recomputed here.
 * opts.onEmailed(result) runs after "Email to customer" sends it.
 */
export function openAppInvoiceDocument(inv, opts = {}) {
  const container = document.getElementById('invoiceContainer1');
  if (!container || !inv) return;
  resetAppInvoiceParts();
  invDocRecord = { app: inv, CustomerId: inv.customerId, onEmailed: opts.onEmailed, payUrl: opts.payUrl || null };

  const setText = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text ?? ''; };
  const show = id => document.getElementById(id)?.classList.remove('hidden');
  const pct = r => `${Number((Number(r || 0) * 100).toFixed(3))}%`;
  const day = ymd => (ymd ? fmtEstDocDate(parseDay(ymd)) : '—');
  const today = formatDateToYYYYMMDD(new Date());
  const open = inv.status === 'sent' || inv.status === 'partial';
  const overdue = open && inv.dueDate && inv.dueDate < today;

  setText('invDocNumber', inv.number || 'DRAFT — not yet issued');
  setText('invDocCustomerName', inv.customerName || '');
  setText('invDocCustomerContact', inv.customerEmail || '');
  setText('invDocBoatName', inv.boatName || '');
  setText('invDocBoatSub', '');
  setText('invDocDateIssued', inv.issuedDate ? day(inv.issuedDate) : '—');
  setText('invDocDueDate', day(inv.dueDate));
  const wos = [...new Set((inv.lines || []).map(l => l.ref?.workOrder).filter(Boolean))];
  setText('invDocWorkOrder', wos.map(w => (/^WO/i.test(w) ? w : `WO-${w}`)).join(', ') || '—');
  document.getElementById('invDocEstimateRef')?.classList.add('hidden');

  const pill = document.getElementById('invDocPastDuePill');
  if (pill) {
    const p = inv.status === 'void' ? ['VOID', 'invDoc-pill-void']
      : inv.status === 'draft' ? ['DRAFT', 'invDoc-pill-draft']
        : inv.status === 'paid' ? ['PAID', 'invDoc-pill-paid']
          : overdue ? [`PAST DUE · ${Math.round((Date.parse(today) - Date.parse(inv.dueDate)) / 86400000)} DAYS`, ''] : null;
    pill.classList.toggle('hidden', !p);
    if (p) { pill.textContent = p[0]; if (p[1]) pill.classList.add(p[1]); }
  }

  const linesBody = document.getElementById('invDocLinesBody');
  if (linesBody) {
    linesBody.innerHTML = (inv.lines || []).map(l => {
      const subs = [];
      if (Number(l.qty) !== 1) subs.push(`${l.qty} × ${formatMoney(l.unitCents)}`);
      if (!l.taxable && inv.taxCents) subs.push('not taxed');
      return `<tr>
        <td><div class="invDoc-line-name">${escapeHTML(l.description)}</div>${subs.length ? `<div class="invDoc-line-sub">${escapeHTML(subs.join(' · '))}</div>` : ''}</td>
        <td class="estDoc-num">${formatMoney(l.amountCents)}</td>
      </tr>`;
    }).join('');
  }

  setText('invDocSubtotal', formatMoney(inv.subtotalCents));
  setText('invDocFeeLabel', `Environmental fee · ${pct(inv.feeRate)}`);
  setText('invDocFee', formatMoney(inv.feeCents));
  setText('invDocTaxLabel', `Sales tax · ${pct(inv.taxRate)}`);
  setText('invDocTax', formatMoney(inv.taxCents));
  setText('invDocInvoiceTotal', formatMoney(inv.totalCents));
  setText('invDocPaid', inv.paidCents ? `-${formatMoney(inv.paidCents)}` : formatMoney(0));
  show('invDocSubtotalRow');
  if (inv.feeCents) show('invDocFeeRow');
  if (inv.taxCents) show('invDocTaxRow');
  if (inv.paidCents) show('invDocPaidRow');
  document.getElementById('invDocDepositRow')?.classList.add('hidden');
  const balance = inv.status === 'void' ? 0 : (inv.balanceCents ?? inv.totalCents);
  setText('invDocBalanceDue', formatMoney(balance));
  setText('invDocPaymentDueBy', open && inv.dueDate ? `Payment due by ${day(inv.dueDate)}` : '');
  if (inv.notes) { setText('invDocNotes', inv.notes); show('invDocNotes'); }
  setText('invDocTerms', inv.status === 'void'
    ? `This invoice was voided${inv.voidReason ? `: ${inv.voidReason}` : ''}. Nothing is owed on it.`
    : `Payment due ${inv.dueDate ? day(inv.dueDate) : 'upon receipt'}. ${opts.payUrl ? `Pay online at ${opts.payUrl}, or reference` : 'Please reference'} ${inv.number || 'this invoice'} with your payment.${opts.termsExtra ? ` ${opts.termsExtra}` : ''}`);
  setText('invDocFooterAddress', shell.letterheadAddress);

  container.classList.remove('hidden');
}

function closeInvoiceDocument() {
  document.getElementById('invoiceContainer1')?.classList.add('hidden');
}

document.getElementById('invDocCloseButton')?.addEventListener('click', closeInvoiceDocument);
document.getElementById('invDocPrintButton')?.addEventListener('click', () => window.print());
document.getElementById('invDocEmailButton')?.addEventListener('click', () => {
  const r = invDocRecord;
  if (!r) return;
  if (r.app) {
    const inv = r.app;
    if (inv.status === 'draft') { toastMessage('Issue the invoice before emailing it.', 'red', 5000); return; }
    if (inv.status === 'void') { toastMessage('This invoice is void.', 'red'); return; }
    const label = `Invoice ${inv.number}`;
    emailDocument({
      pages: [...document.querySelectorAll('#invoiceContainer1 .invDoc-page')],
      label, customerId: inv.customerId, docType: 'invoice', recordType: 'invoice', recordId: inv.id || null,
      templateId: 'invoice',
      vals: {
        invoice_no: inv.number, balance: formatMoney(inv.balanceCents ?? inv.totalCents),
        due_date: inv.dueDate ? fmtEstDocDate(parseDay(inv.dueDate)) : 'on receipt',
        ...(inv.boatName ? { boat: inv.boatName } : {}),
      },
      links: r.payUrl ? { pay_link: r.payUrl } : {},
      onSent: res => r.onEmailed?.(res),
      afterSend: inv.id ? { kind: 'invoiceEmailed', id: inv.id } : null,
    });
    return;
  }
  const label = `Invoice INV-${r.Id}`;
  const balance = document.getElementById('invDocBalanceDue')?.textContent || '';
  emailDocument({
    pages: [...document.querySelectorAll('#invoiceContainer1 .invDoc-page')],
    label, customerId: r.CustomerId, docType: 'invoice', recordType: 'invoice', recordId: r.Id ? String(r.Id) : null,
    templateId: 'invoice',
    vals: {
      invoice_no: `INV-${r.Id}`, ...(balance ? { balance } : {}),
      due_date: r.DueDate ? fmtEstDocDate(parseDay(r.DueDate)) : 'on receipt',
      ...(r.BoatName ? { boat: r.BoatName } : {}),
    },
  });
});

document.getElementById('invDocEmailsButton')?.addEventListener('click', () => {
  const r = invDocRecord;
  if (!r) return;
  const id = r.app ? r.app.id : (r.Id ? String(r.Id) : null);
  if (!id) return;
  const inv = r.app || {};
  openEmailHistory({ recordId: id }, {
    title: r.app ? `Invoice ${inv.number}` : `Invoice INV-${r.Id}`,
    sub: [inv.customerName || r.CustomerName, inv.boatName || r.BoatName].filter(Boolean).join(' · '),
  });
});

document.getElementById('estimate-print-btn')?.addEventListener('click', async () => {
  if (!estimateState.Operations.length) {
    toastMessage('Add at least one operation first', 'red');
    return;
  }
  const customerId = document.getElementById('work-order-form-customer-id').value;
  const boatId = document.getElementById('work-order-form-boat-id').value;
  const pricing = recalcEstimatePricing();
  if (!estimateState.Estimate_Number) {
    try {
      estimateState.Estimate_Number = await nextEstimateNumber();
      const numberDisplay = document.getElementById('estimate-number-display');
      if (numberDisplay) numberDisplay.textContent = estimateState.Estimate_Number;
    } catch (e) {
      console.error('Error assigning estimate number for preview:', e);
      toastMessage('Could not assign an estimate number — try again', 'red');
      return;
    }
  }
  const record = buildEstimateRecordFromState(customerId, boatId, pricing);
  openEstimateDocument(record);
});

// ---------- Saved estimates list ----------

// Live (docs/live-updates-plan.md): while the list or an estimate is open, /estimates is listened
// to (one download, then only changes; paused while the tab is hidden, off once both are closed).
// The list patches row by row and the open estimate follows another person's change in place.
let estimatesLive = null;
let shownEstimateId = null;
const isShown = (id) => !document.getElementById(id)?.classList.contains('hidden');

function startEstimatesLive() {
  if (estimatesLive) return;
  estimatesLive = liveScope();
  let settleAt = 0;
  const pending = new Set();
  let timer = null;
  const queue = (id) => {
    pending.add(id);
    clearTimeout(timer);
    timer = setTimeout(() => {
      const ids = [...pending];
      pending.clear();
      applyEstimateChanges(ids, Date.now() > settleAt);
    }, 200);
  };
  const put = async (snap) => {
    const v = snap.val();
    // /estimates/lastUpdated is a sync timestamp, not an estimate.
    if (!v || typeof v !== 'object') return;
    await idbPut('estimateData', { ...v, id: snap.key });
    queue(snap.key);
  };
  estimatesLive.add(() => {
    settleAt = Date.now() + 4000; // the first replay is the cache catching up — no glow
    const r = ref(database, 'estimates');
    const offs = [
      onChildAdded(r, put, () => {}),
      onChildChanged(r, put, () => {}),
      onChildRemoved(r, async (snap) => { await idbDelete('estimateData', snap.key); queue(snap.key); }, () => {}),
    ];
    return () => offs.forEach(off => off());
  });
}

function stopEstimatesLiveIfClosed() {
  if (estimatesLive && !isShown('estimates-list-overlay') && !isShown('estimate-detail-overlay')) {
    estimatesLive.close();
    estimatesLive = null;
  }
}

async function applyEstimateChanges(ids, glowThem) {
  const lively = glowThem && !recentInteraction();
  if (isShown('estimates-list-overlay')) {
    await renderEstimatesList(document.getElementById('estimates-list-search')?.value || '', lively ? ids : []);
  }
  if (shownEstimateId && ids.includes(shownEstimateId) && isShown('estimate-detail-overlay')) {
    if (pointerHeld()) { setTimeout(() => applyEstimateChanges([shownEstimateId], glowThem), 800); return; }
    const record = await idbGet('estimateData', shownEstimateId);
    if (!record) {
      toastMessage('This estimate was just deleted by someone else.', 'red');
      return;
    }
    renderEstimateDetail(record);
    const body = document.getElementById('estimate-detail-body');
    if (lively && body) { body.dataset.key = shownEstimateId; glow([body]); }
  }
}

// The device copy of /estimates is otherwise kept only while the list or an estimate is open
// (startEstimatesLive), so the rail's search brings it up to date itself — at most every 10 minutes.
let estimatesSyncedAt = 0;
const ESTIMATES_SYNC_MS = 10 * 60 * 1000;
export async function syncEstimatesToDevice() {
  if (estimatesLive || Date.now() - estimatesSyncedAt < ESTIMATES_SYNC_MS) return false;
  estimatesSyncedAt = Date.now();
  try {
    const all = (await get(ref(database, 'estimates'))).val() || {};
    const keep = Object.entries(all).filter(([, v]) => v && typeof v === 'object');
    await Promise.all(keep.map(([id, v]) => idbPut('estimateData', { ...v, id })));
    const ids = new Set(keep.map(([id]) => id));
    const local = await shell.fetchDataFromStore('estimateData');
    await Promise.all(local.filter(r => r?.id && !ids.has(r.id)).map(r => idbDelete('estimateData', r.id)));
    return true;
  } catch {
    estimatesSyncedAt = 0; // offline or no access: the device copy stays, try again next time
    return false;
  }
}

/** Open one estimate by id (the rail's search). */
export async function openEstimateById(id) {
  const record = (await shell.fetchDataFromStore('estimateData')).find(r => r && r.id === id);
  if (record) openEstimateDetail(record);
}

export async function openEstimatesList() {
  const overlay = document.getElementById('estimates-list-overlay');
  const search = document.getElementById('estimates-list-search');
  if (!overlay) return;
  overlay.classList.remove('hidden');
  if (search) search.value = '';
  await renderEstimatesList('');
  startEstimatesLive();
}

function closeEstimatesList() {
  document.getElementById('estimates-list-overlay')?.classList.add('hidden');
  stopEstimatesLiveIfClosed();
}

async function renderEstimatesList(query, glowIds = []) {
  const body = document.getElementById('estimates-list-body');
  const countEl = document.getElementById('estimates-list-count');
  if (!body) return;

  // Older syncs cached /estimates/lastUpdated as a blank row; skip anything that isn't an estimate.
  const all = (await shell.fetchDataFromStore('estimateData')).filter(r => r && r.id !== 'lastUpdated');
  all.sort((a, b) => new Date(b.Updated_Date || b.Created_Date || 0) - new Date(a.Updated_Date || a.Created_Date || 0));

  const terms = (query || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
  const filtered = terms.length
    ? all.filter(r => {
        const values = [r.Estimate_Number, r.Customer_Name, r.Boat_Name].map(v => (v || '').toString().toLowerCase());
        return terms.every(term => values.some(v => v.includes(term)));
      })
    : all;

  if (countEl) countEl.textContent = String(filtered.length);

  if (!filtered.length) {
    patchHTML(body, `<div class="wo-picker-empty" data-key="empty">No estimates yet.</div>`);
    return;
  }

  patchHTML(body, filtered.map(r => `
    <div class="estimate-list-row" data-estimate-id="${escapeHTML(r.id)}" data-key="${escapeHTML(r.id)}">
      <span class="estimate-list-row-number">${escapeHTML(r.Estimate_Number || '')}</span>
      <div class="estimate-list-row-main">
        <div class="estimate-list-row-name">${escapeHTML(r.Customer_Name || '')} &middot; ${escapeHTML(r.Boat_Name || '')}</div>
        <div class="estimate-list-row-sub">${escapeHTML(r.Short_Description || '')}</div>
      </div>
      <span class="wo-status-pill wo-status-pill-${(r.Status || 'Draft').toLowerCase()}">${escapeHTML(r.Status || 'Draft')}</span>
      <span class="estimate-list-row-total">$${Number(r.Pricing?.total || 0).toFixed(2)}</span>
    </div>
  `).join(''));

  body.querySelectorAll('.estimate-list-row').forEach(el => {
    el.onclick = () => {
      const record = filtered.find(r => r.id === el.dataset.estimateId);
      if (record) {
        // Detail first, so closing the list doesn't stop the listener the detail still needs.
        openEstimateDetail(record);
        closeEstimatesList();
      }
    };
  });
  if (glowIds.length) glow([...body.querySelectorAll('.estimate-list-row')].filter(el => glowIds.includes(el.dataset.estimateId)));
}

document.getElementById('estimates-list-button')?.addEventListener('click', openEstimatesList);
// Sign-by-link history, signed copies and reminders (signLog.js, roadmap 4.2).
document.getElementById('estimates-sign-log-btn')?.addEventListener('click', () => openSignLog({ kind: 'estimate' }));
document.getElementById('estimates-list-close')?.addEventListener('click', closeEstimatesList);
document.getElementById('estimates-list-overlay')?.addEventListener('click', (e) => {
  if (e.target.id === 'estimates-list-overlay') closeEstimatesList();
});

let estimatesListSearchDebounce = null;
document.getElementById('estimates-list-search')?.addEventListener('input', function () {
  clearTimeout(estimatesListSearchDebounce);
  const query = this.value;
  estimatesListSearchDebounce = setTimeout(() => renderEstimatesList(query), 150);
});

// ---------- Estimate detail + lifecycle ----------

function openEstimateDetail(record) {
  renderEstimateDetail(record);
  document.getElementById('estimate-detail-overlay')?.classList.remove('hidden');
  if (record?.id) startEstimatesLive();
}

function closeEstimateDetail() {
  document.getElementById('estimate-detail-overlay')?.classList.add('hidden');
  shownEstimateId = null;
  stopEstimatesLiveIfClosed();
}

function renderEstimateDetail(record) {
  const title = document.getElementById('estimate-detail-title');
  const pill = document.getElementById('estimate-detail-status-pill');
  const body = document.getElementById('estimate-detail-body');
  const actions = document.getElementById('estimate-detail-actions');
  if (!body || !actions) return;
  shownEstimateId = record.id || null;

  if (title) title.textContent = record.Estimate_Number || 'Estimate';
  if (pill) {
    pill.textContent = record.Status || 'Draft';
    pill.className = 'wo-status-pill wo-status-pill-' + (record.Status || 'Draft').toLowerCase();
  }

  const pricing = record.Pricing || {};
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const opsHtml = (record.Operations || []).filter(Boolean).map(op => {
    const answer = decisionLabel(op);
    const amt = op.Customer_Decision === 'approved' && op.Option ? op.Option_Amount : op.Amount;
    return `
    <div class="estimate-detail-op-row${op.Customer_Decision === 'declined' ? ' declined' : ''}"><span>${escapeHTML(op.Description || '')}${answer ? ` <em class="estimate-detail-answer estimate-detail-answer-${op.Customer_Decision}">${escapeHTML(answer)}</em>` : ''}${(op.Options || []).length && !answer ? `<em class="estimate-detail-answer">${op.Options.length} choices</em>` : ''}</span><span>${money(amt)}</span></div>`;
  }).join('') || '<div class="estimate-detail-op-row"><span>No operations</span><span></span></div>';
  const ap = record.Approved_Pricing;
  const partsHtml = (record.Parts || []).map(p => `
    <div class="estimate-detail-part-row"><span>${escapeHTML(p.Description || '')} &times;${Number(p.Qty || 0)}</span><span>${money(Number(p.Qty || 0) * Number(p.Unit_Price || 0))}</span></div>
  `).join('') || '<div class="estimate-detail-part-row"><span>No parts</span><span></span></div>';

  patchHTML(body, `
    <div class="estimate-detail-context">
      <div class="estimate-detail-context-name">${escapeHTML(record.Customer_Name || '')} &middot; ${escapeHTML(record.Boat_Name || '')}</div>
      <div class="estimate-detail-context-sub">${escapeHTML(record.Short_Description || '')}</div>
    </div>
    <div>
      <div class="estimate-detail-section-label">Operations</div>
      ${opsHtml}
    </div>
    <div>
      <div class="estimate-detail-section-label">Parts</div>
      ${partsHtml}
    </div>
    <div class="estimate-detail-totals">
      <div class="estimate-detail-totals-row"><span>Labor</span><span>${money(pricing.laborTotal)}</span></div>
      <div class="estimate-detail-totals-row"><span>Parts</span><span>${money(pricing.partsTotal)}</span></div>
      <div class="estimate-detail-totals-row"><span>Fee + tax</span><span>${money((pricing.environmentalFee || 0) + (pricing.salesTax || 0))}</span></div>
      <div class="estimate-detail-totals-row total"><span>Total</span><span>${money(pricing.total)}</span></div>
      <div class="estimate-detail-totals-row"><span>Deposit due</span><span>${money(pricing.depositDue)}</span></div>
      ${ap ? `<div class="estimate-detail-totals-row total"><span>Approved by the customer</span><span>${money(ap.total)}</span></div>
      <div class="estimate-detail-totals-row"><span>Deposit on what was approved</span><span>${money(ap.depositDue)}</span></div>` : ''}
    </div>
    ${record.Partial ? '<div class="estimate-detail-convert-note">Answered line by line in the portal. Declined items were saved on the boat for later (Customers › boat › Saved for later); converting makes only the approved ones.</div>' : ''}
    ${depositNoteHTML(record)}
    ${record.Converted_WO_Number ? `<div class="estimate-detail-convert-note">Converted to work order <b>#${escapeHTML(String(record.Converted_WO_Number))}</b></div>` : ''}
  `);

  actions.innerHTML = '';
  const addAction = (label, onClick, cls) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    if (cls) btn.className = cls;
    btn.addEventListener('click', onClick);
    actions.appendChild(btn);
    return btn;
  };

  addAction('Print / PDF', () => openEstimateDocument(record));
  if (record.Signed_Link?.pdfPath) {
    // The copy the customer signed by link, with their signature and the audit trail (roadmap 4.2).
    addAction('Signed copy', async () => {
      const tab = window.open('about:blank', '_blank');
      try {
        const url = await getDownloadURL(storageRef(storage, record.Signed_Link.pdfPath));
        if (tab) tab.location.href = url; else window.location.href = url;
      } catch (err) { tab?.close(); toastMessage(String(err.message || err), 'red', 6000); }
    });
  }

  switch (record.Status) {
    case 'Draft':
      addAction('Mark as sent', () => transitionEstimateStatus(record, 'Sent'), 'primary');
      break;
    case 'Sent':
      if (record.id) {
        addAction('Copy approval link', async () => {
          try {
            const r = await callFn('createSignLink', { kind: 'estimate', estimateId: record.id });
            await navigator.clipboard.writeText(r.url).catch(() => window.prompt('Approval link (copy it):', r.url));
            toastMessage('Approval link copied: the customer can review and sign without signing in', 'green', 5000);
          } catch (err) { toastMessage(String(err.message || err), 'red', 6000); }
        });
      }
      addAction('Declined', () => transitionEstimateStatus(record, 'Declined'), 'danger');
      addAction('Expired', () => transitionEstimateStatus(record, 'Expired'), 'danger');
      addAction('Accepted', () => transitionEstimateStatus(record, 'Accepted'), 'primary');
      break;
    case 'Accepted':
      addDepositActions(record, addAction);
      if (record.Converted_WO_Number) {
        addAction('Already converted', () => {}, 'primary').disabled = true;
      } else {
        addAction('Convert to work order', () => convertEstimateToWorkOrder(record), 'primary');
      }
      break;
    case 'Declined':
    case 'Expired':
      addAction('Duplicate as new draft', () => duplicateEstimate(record), 'primary');
      break;
  }
}

document.getElementById('estimate-detail-close')?.addEventListener('click', closeEstimateDetail);
document.getElementById('estimate-detail-overlay')?.addEventListener('click', (e) => {
  if (e.target.id === 'estimate-detail-overlay') closeEstimateDetail();
});

/* ---------- deposits (roadmap 2.3, functions/payLinks.js) ---------- */
// Approving in the portal asks for the estimate's deposit (Deposit_Percent) and offers the customer
// a pay link. A paid deposit is a payment kept as credit, which comes off the final invoice when
// the office applies it (invoice › Apply credit). Here the office sees where it stands, can ask
// for it (approval taken by phone), record it paid by check/cash, or cancel the request.
const callFn = (name, data) => httpsCallable(functions, name, { timeout: 60000 })(data).then(r => r.data);

function depositNoteHTML(record) {
  const s = record.Signed_Link;
  const signed = s ? `<div class="estimate-detail-convert-note">${s.decision === 'decline' ? 'Declined' : 'Approved and signed'} by email link${s.name ? ` by ${escapeHTML(s.name)}` : ''} on ${new Date(s.at).toLocaleString()}.</div>` : '';
  return signed + depositOnlyHTML(record);
}

function depositOnlyHTML(record) {
  const d = record.Deposit;
  if (!d || usesDockmaster()) return '';
  const amt = formatMoney(d.amountCents);
  const text = d.status === 'paid' ? `Deposit of ${amt} received${d.paymentNumber ? ` (${escapeHTML(d.paymentNumber)})` : ''}. It’s on the customer’s account as credit for the final invoice.`
    : d.status === 'cancelled' ? `Deposit request (${amt}) cancelled.`
      : `Deposit of ${amt} requested${d.requestedAt ? ` ${new Date(d.requestedAt).toLocaleDateString()}` : ''}, not paid yet.`;
  return `<div class="estimate-detail-convert-note">${text}</div>`;
}

function addDepositActions(record, addAction) {
  if (usesDockmaster() || !record.id || !(Number(record.Pricing?.depositDue) > 0)) return;
  const d = record.Deposit;
  const refresh = patch => { record.Deposit = { ...(record.Deposit || {}), ...patch }; renderEstimateDetail(record); };
  if (!d || d.status === 'requested') {
    addAction(d ? 'Copy deposit link' : 'Ask for deposit', async () => {
      try {
        const r = await callFn('requestEstimateDeposit', { estimateId: record.id });
        refresh({ depositId: r.depositId, amountCents: r.amountCents, status: r.status, requestedAt: record.Deposit?.requestedAt || Date.now() });
        if (r.url) {
          await navigator.clipboard.writeText(r.url).catch(() => window.prompt('Deposit pay link (copy it):', r.url));
          toastMessage('Deposit pay link copied: send it to the customer', 'green', 5000);
        } else {
          toastMessage('Deposit requested. Online payment isn’t set up, so record it here when it’s paid.', 'green', 6000);
        }
      } catch (err) { toastMessage(String(err.message || err), 'red', 6000); }
    });
  }
  if (d && d.status === 'requested') {
    addAction('Deposit received…', async () => {
      const method = (window.prompt('How was the deposit paid? (check, cash or card)', 'check') || '').trim().toLowerCase();
      if (!method) return;
      if (!['check', 'cash', 'card'].includes(method)) { toastMessage('Type check, cash or card.', 'red'); return; }
      const reference = method === 'cash' ? '' : (window.prompt(method === 'check' ? 'Check number (optional)' : 'Last 4 of the card (optional)', '') || '');
      try {
        const p = await callFn('recordDepositByHand', { depositId: d.depositId, method, reference });
        refresh({ status: 'paid', paymentNumber: p.number, paymentId: p.id });
        toastMessage(`Deposit recorded (${p.number})`, 'green');
      } catch (err) { toastMessage(String(err.message || err), 'red', 6000); }
    });
    addAction('Cancel deposit request', async () => {
      if (!window.confirm('Cancel the deposit request? The customer’s pay link will say nothing is owed.')) return;
      try {
        await callFn('cancelDeposit', { depositId: d.depositId });
        refresh({ status: 'cancelled' });
      } catch (err) { toastMessage(String(err.message || err), 'red', 6000); }
    }, 'danger');
  }
}

async function transitionEstimateStatus(record, newStatus) {
  if (blockIfReadOnly('Updating estimate status')) return;
  record.Status = newStatus;
  record.Updated_Date = new Date().toISOString();
  try {
    await saveEstimateRecord(record);
    toastMessage(`${record.Estimate_Number} marked ${newStatus}`, 'green');
    renderEstimateDetail(record);
  } catch (e) {
    console.error('Error updating estimate status:', e);
    toastMessage('Error updating estimate status', 'red');
  }
}

// Declined/expired estimates keep their document and history; duplicating
// starts a fresh Draft with a new EST- number and re-reads part prices from
// current inventory (per spec — the old quote's prices don't carry over).
async function duplicateEstimate(record) {
  if (blockIfReadOnly('Duplicating estimate')) return;
  try {
    const inventory = await shell.fetchDataFromStore('inventoryData');
    const invById = new Map(inventory.map(i => [i.id, i]));

    const newParts = (record.Parts || []).map(p => {
      const fresh = !p.manual && p.inventoryId ? invById.get(p.inventoryId) : null;
      return { ...p, id: generateId(), Unit_Price: fresh ? (Number(fresh.Sale_Price) || 0) : Number(p.Unit_Price || 0) };
    });
    const newOperations = (record.Operations || []).map(o => ({ ...o, id: generateId() }));

    const laborTotal = newOperations.reduce((sum, o) => sum + Number(o.Amount || 0), 0);
    const partsTotal = newParts.reduce((sum, p) => sum + Number(p.Qty || 0) * Number(p.Unit_Price || 0), 0);
    const subtotal = laborTotal + partsTotal;
    const envFeeRate = estimateDefaults.environmentalFeeRate;
    const taxRate = estimateDefaults.salesTaxRate;
    const envFee = subtotal * envFeeRate;
    const tax = subtotal * taxRate;
    const total = subtotal + envFee + tax;
    const depositPercent = record.Deposit_Percent || 0;
    const depositDue = total * (depositPercent / 100);

    const newRecord = {
      ...record,
      id: generateId(),
      Estimate_Number: await nextEstimateNumber(),
      Status: 'Draft',
      Operations: newOperations,
      Parts: newParts,
      Pricing: { laborTotal, partsTotal, environmentalFeeRate: envFeeRate, environmentalFee: envFee, salesTaxRate: taxRate, salesTax: tax, total, depositPercent, depositDue },
      Created_Date: new Date().toISOString(),
      Updated_Date: new Date().toISOString(),
      Converted_WO_Number: null,
      Converted_Date: null,
    };

    await saveEstimateRecord(newRecord);
    toastMessage(`Duplicated as ${newRecord.Estimate_Number}`, 'green');
    openEstimateDetail(newRecord);
  } catch (e) {
    console.error('Error duplicating estimate:', e);
    toastMessage('Error duplicating estimate', 'red');
  }
}

// One click: creates a real work order (pulling the Dockmaster WO # now,
// for the first time) with every operation and the parts list attached,
// deducts inventory-linked part stock, and links the estimate to it.
// Reuses the existing data/{Job_ID} + data/{Job_ID}/Parts/{id} shapes so
// converted work shows up everywhere a normal work order does.
async function convertEstimateToWorkOrder(record) {
  if (blockIfReadOnly('Converting to a work order')) return;
  if (record.Status !== 'Accepted') {
    toastMessage('Only accepted estimates can convert', 'red');
    return;
  }
  if (record.Converted_WO_Number) {
    toastMessage('This estimate has already been converted', 'red');
    return;
  }
  if (usesDockmaster() && (!record.Customer_DMid || !record.Boat_DMid)) {
    toastMessage('This customer/boat has no Dockmaster ID on file', 'red');
    return;
  }

  try {
    const clerkId = shell.globalUserData[localStorage.getItem('currentUserId')].clerkId;
    const workOrderNumber = await shell.getWorkOrderNumberMove(
      clerkId, record.Customer_DMid, record.Boat_DMid, 'I', record.Short_Description || undefined
    );

    const today = formatDateToYYYYMMDD(new Date());
    const jobIds = [];

    for (const op of (record.Operations || []).filter(o => o && o.Customer_Decision !== 'declined')) {
      const jobId = generateId();
      jobIds.push(jobId);
      const serviceLog = {
        id: jobId,
        Job_ID: jobId,
        Boat_ID: record.Boat_ID,
        Boat_Name: record.Boat_Name,
        Customer_ID: record.Customer_ID,
        Customer_Name: record.Customer_Name,
        Category: 'General Labor',
        OpCode: op.OpCode || '',
        Op_Type: op.Op_Type || '',
        Description: op.Option ? `${op.Description} (${op.Option})` : (op.Description || ''),
        Short_Description: op.Option ? `${op.Description} (${op.Option})` : (op.Description || ''),
        Short_Description_DM: op.Option ? `${op.Description} (${op.Option})` : (op.Description || ''),
        Estimated_Hours: op.Hours || 0,
        Operation_Dependency: '',
        Request_Date: today,
        Schedule_Date: '',
        Haul_Date: '',
        Launch_Date: '',
        Start_Date: today,
        Status: 'On Work Schedule',
        Techs: '',
        Email: '',
        Notes: `Converted from ${record.Estimate_Number}`,
        Work_Order: workOrderNumber,
      };
      await shell.addJobFirebase(serviceLog);
      await shell.updateOpenDataDB(serviceLog);
    }

    const primaryJobId = jobIds[0];
    const stockDeductionFailures = [];
    if (primaryJobId) {
      const deductedBy = (await shell.getCurrentUserData())?.displayName || 'Unknown';
      for (const part of (record.Parts || [])) {
        const partId = generateId();
        const partData = {
          Part_Number: part.Part_Number || '',
          Quantity: part.Qty || 0,
          Category: 'General Labor',
          Op_Type: '',
          Job_ID: primaryJobId,
          ID: partId,
          Boat_Name: record.Boat_Name,
          Customer_Name: record.Customer_Name,
          Time_Submitted: today,
          Work_Order: workOrderNumber,
          Tech: '',
          Description: part.Description || ''
        };
        await shell.addPartsFirebase(partData);
        if (!part.manual && part.inventoryId) {
          try {
            const usageRef = `${workOrderNumber} · ${record.Boat_Name} — ${part.Description || part.Part_Number || 'part'}`;
            await shell.reduceInventory(part.inventoryId, Number(part.Qty) || 0, usageRef, deductedBy);
          } catch (stockError) {
            console.error('Stock deduction failed for', part.Part_Number, stockError);
            stockDeductionFailures.push(part.Part_Number || part.Description || 'part');
          }
        }
      }
    }

    record.Converted_WO_Number = workOrderNumber;
    record.Converted_Date = new Date().toISOString();
    record.Updated_Date = new Date().toISOString();
    await saveEstimateRecord(record);

    toastMessage(`Work order #${workOrderNumber} created from ${record.Estimate_Number}`, 'green');
    if (stockDeductionFailures.length) {
      toastMessage(`Stock not deducted for: ${stockDeductionFailures.join(', ')} — adjust inventory manually`, 'red', 6000);
    }
    renderEstimateDetail(record);
  } catch (e) {
    console.error('Error converting estimate to work order:', e);
    toastMessage('Error converting estimate to work order', 'red');
  }
}
